// Burst de destellos al enviar un reconocimiento desde el perfil de un compañero (maqueta:
// ReconocimientoPage.tsx del demo, líneas 7-46). 28 partículas en patrón radial, 25% estrellas /
// 75% círculos, con color/tamaño/ángulo/distancia aleatorios. Se auto-destruye a los 1400ms.
import { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';

const SPARKLE_COUNT = 28;
const SPARKLE_COLORS = ['#F08521', '#FFD700', '#ffffff', '#FFE566', '#342F82', '#F08521', '#20B15A'];
const DONE_DELAY_MS = 1400;

interface SparkleParticle {
  id: number;
  angle: number;
  dist: number;
  size: number;
  color: string;
  delay: number;
  duration: number;
  shape: 'star' | 'circle';
}

interface SparkleOverlayProps {
  onDone: () => void;
}

/** Burst radial de 28 partículas — igual patrón que el prototipo (SPARKLE_COUNT/SPARKLE_COLORS). */
export function SparkleOverlay({ onDone }: Readonly<SparkleOverlayProps>) {
  useEffect(() => {
    const timer = setTimeout(onDone, DONE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [onDone]);

  const sparks = useMemo<SparkleParticle[]>(() => Array.from({ length: SPARKLE_COUNT }, (_, idx) => ({
    id: idx,
    angle: (idx / SPARKLE_COUNT) * 360 + (Math.random() - 0.5) * 20,
    dist: 100 + Math.random() * 180,
    size: 5 + Math.random() * 9,
    color: SPARKLE_COLORS[idx % SPARKLE_COLORS.length],
    delay: Math.random() * 0.15,
    duration: 0.9 + Math.random() * 0.3,
    shape: idx % 4 === 0 ? 'star' : 'circle',
  })), []);

  return (
    <div className="sparkle-overlay">
      {sparks.map((spark) => {
        const radians = spark.angle * (Math.PI / 180);
        return (
          <motion.div
            key={spark.id}
            className={`sparkle-p ${spark.shape === 'star' ? 'sparkle-p--star' : ''}`}
            style={{ width: spark.size, height: spark.size, background: spark.color, left: '50%', top: '50%' }}
            initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
            animate={{ x: Math.cos(radians) * spark.dist, y: Math.sin(radians) * spark.dist, scale: [0, 1.2, 0], opacity: [0, 1, 0] }}
            transition={{ duration: spark.duration, delay: spark.delay, ease: 'easeOut' }}
          />
        );
      })}
      <motion.div
        className="sparkle-center"
        initial={{ scale: 0, opacity: 1 }}
        animate={{ scale: [0, 1.6, 0], opacity: [0, 1, 0] }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        ✨
      </motion.div>
    </div>
  );
}

export default SparkleOverlay;
